import {
    Component,
    computed,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Signal,
    SimpleChanges,
    ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MdsButtonComponent } from '@medpacesoftwaredevelopment/designsystem';
import { SelectedCustomControl } from '@models/selected-custom-control';
import { _getInputControls } from '@utility/utility';
import { isArray } from 'lodash-es';
import { combineLatest, filter, map, startWith, Subject, takeUntil, tap } from 'rxjs';
import { SelectedCustomDatePickerComponent } from './selected-custom-datepicker.component';
import { SelectedCustomDropDownComponent } from './selected-custom-dropdown.component';
@Component({
    selector: 'selected-custom',
    standalone: true,
    imports: [MdsButtonComponent, SelectedCustomDropDownComponent, SelectedCustomDatePickerComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: 'selected-custom.component.html',
})
export class SelectedCustomComponent implements OnChanges, OnDestroy {
    readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(() => this.router.url),
            startWith(this.router.url)
        ),
        { initialValue: this.router.url }
    );

    createRows: SelectedCustomControl[] = [];
    setToDefaults: boolean = false;
    isPatientOverviewUrl: Signal<boolean> = computed(
        () =>
            this.currentUrl().includes('studies') &&
            this.currentUrl().includes('sites') &&
            this.currentUrl().includes('patients')
    );
    isPatientAllUrl: Signal<boolean> = computed(
        () => this.currentUrl().includes('patients') && !this.currentUrl().includes('sites')
    );
    isManageViewUrl: Signal<boolean> = computed(() => this.currentUrl().includes('manage'));
    isSiteUrl: Signal<boolean> = computed(
        () => this.currentUrl().includes('sites') && !this.currentUrl().includes('patients')
    );

    private genericRange = {};
    private selectControls: SelectedCustomControl[];
    private patientId: number;
    private filters: {};
    private componentDestroyed$: Subject<boolean> = new Subject();
    private queryFilters = {};
    private filtersModifiedByUser: boolean = false;

    @Input() data: any[];
    @Output() filterClickEvent = new EventEmitter();
    @Output() resetDataClickEvent = new EventEmitter();
    allOption = {
        val: 'all',
        viewValue: 'ALL',
    };
    constructor(
        private router: Router,
        private route: ActivatedRoute
    ) {}

    urlValueChange = toSignal(
        combineLatest({ params: this.route.params, queryParams: this.route.queryParamMap }).pipe(
            takeUntil(this.componentDestroyed$),
            tap((result) => {
                this.patientId = result?.params?.patientId;
                this.queryFilters = {};

                if (result?.queryParams?.keys.length !== 0) {
                    const params = JSON.parse(result?.queryParams?.get('params'));
                    let statusFilters = params['status'];
                    if (statusFilters) {
                        this.queryFilters['status'] = statusFilters;
                    }
                    statusFilters = params['statusid'];
                    if (statusFilters) {
                        this.queryFilters['statusid'] = statusFilters;
                    }

                    this.genericRange = this.queryFilters;
                }

                this.setFilters();
            })
        )
    );

    ngOnDestroy(): void {
        if (this.filtersModifiedByUser) {
            const dataToStore = { ...this.genericRange, id: this.patientId };

            localStorage.setItem(
                this.getFilterName(),
                Object.keys(dataToStore).length > 0 ? JSON.stringify(dataToStore) : null
            );
        }

        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();
    }

    getLocalStorageData() {
        const storeData = JSON.parse(localStorage.getItem(this.getFilterName()));

        if (this.isPatientOverviewUrl() && storeData?.id !== this.patientId) return null;
        return storeData;
    }

    setFilters() {
        this.filters = this.isEmpty(this.queryFilters) ? this.getLocalStorageData() : this.queryFilters;
        if (this.selectControls) {
            this.selectControls.forEach((element) => {
                element.selectedValues = [];
            });

            if (this.filters) {
                let arrayKey = Object.keys(this.filters);
                arrayKey.forEach((key) => {
                    let baseKey = key;
                    if (key === 'requesttype') key = 'request type';
                    if (key === 'visitname') key = 'visit name';
                    if (key === 'visitdate') key = 'visit date';
                    if (key === 'requestdate') key = 'request date';
                    if (key === 'statusid') key = 'status';
                    if (key === 'patientNum') key = 'patient id';
                    if (key === 'requesttimestamp') key = 'request timestamp';
                    if (key === 'closetimestamp') key = 'close timestamp';
                    if (key === 'processedby') key = 'processed by';

                    let data = this.selectControls.find((x) => x.labelText.toLowerCase() === key);
                    if (data) {
                        if (data.values !== null && data.values.some((x) => this.filters[baseKey].includes(x.value))) {
                            data.selectedValues = this.filters[baseKey];
                        }
                    }
                });
            } else {
                this.selectControls.forEach((element) => {
                    if (element.defaultSelectedValues != undefined)
                        element.selectedValues = element.defaultSelectedValues;
                });
            }
        }
    }

    /* istanbul ignore next */
    ngOnChanges(changes: SimpleChanges) {
        if (changes.data && changes.data.currentValue?.length > 0) {
            let controls = _getInputControls('');
            if (this.isPatientAllUrl()) {
                controls = _getInputControls('patientAll');

                const countryControl = controls?.find((p) => p.labelText === 'Country');
                countryControl.values = Array.from(
                    new Set(this.data?.filter((item) => item.country !== 'Removed').map((item) => item.country))
                ).map((el) => ({
                    value: el,
                    viewValue: el,
                }));

                //status has patient data
                const statusControl = controls?.find((p) => p.labelText === 'Status');
                statusControl.values = [...new Set(this.data?.map((item) => item.statusId))].map((el) => ({
                    value: el,
                    viewValue: this.data?.find((val) => val.statusId === el).statusName,
                }));
            } else if (this.isManageViewUrl()) {
                controls = _getInputControls('accessRequests');

                const statusControl = controls?.find((p) => p.labelText === 'Status');
                statusControl.values = [...new Set(this.data?.map((item) => item.status))].map((el) => ({
                    value: el,
                    viewValue: el,
                }));

                const accessTypeControl = controls?.find((p) => p.labelText === 'Access Type');
                accessTypeControl.values = [...new Set(this.data?.map((item) => item.accessType))]
                    .filter((el) => !!el)
                    .map((el) => ({
                        value: el,
                        viewValue: el,
                    }));

                const processedByControl = controls?.find((p) => p.labelText === 'Processed By');
                processedByControl.values = [...new Set(this.data?.map((item) => item.processedBy))].map((el) => ({
                    value: el,
                    viewValue: el,
                }));
            } else if (this.isSiteUrl()) {
                controls = _getInputControls('crcSites');

                const countryControl = controls?.find((p) => p.labelText === 'Country');
                countryControl.values = Array.from(
                    new Set(this.data?.filter((item) => item.country !== 'Removed').map((item) => item.country))
                ).map((el) => ({
                    value: el,
                    viewValue: el,
                }));

                const protocolControl = controls?.find((p) => p.labelText === 'Protocol');
                protocolControl.values = Array.from(new Set(this.data?.map((item) => item.protocol))).map((el) => ({
                    value: el,
                    viewValue: el,
                }));

                //status has patient data
                const statusControl = controls?.find((p) => p.labelText === 'Status');
                statusControl.values = [...new Set(this.data?.map((item) => item.statusId))].map((el) => ({
                    value: el,
                    viewValue: this.data?.find((val) => val.statusId === el).statusName,
                }));
            } else {
                const visitNameControl = controls?.find((p) => p.labelText === 'Visit Name');
                visitNameControl.values = Array.from(new Set(this.data?.map((item) => item.visitName))).map((el) => ({
                    value: el,
                    viewValue: el,
                }));

                const requestTypeControl = controls?.find((p) => p.labelText === 'Request Type');
                requestTypeControl.values = Array.from(new Set(this.data?.map((item) => item.requestType))).map(
                    (el) => ({ value: el, viewValue: el })
                );

                //status has request data
                const statusControl = controls?.find((p) => p.labelText === 'Status');
                statusControl.values = [...new Set(this.data?.map((item) => item.status))].map((el) => ({
                    value: el,
                    viewValue: el,
                }));

                if (!this.isPatientOverviewUrl()) {
                    controls = _getInputControls('requests');

                    const patientControl = controls?.find((p) => p.labelText === 'Patient ID');
                    patientControl.values = Array.from(new Set(this.data?.map((item) => item.patientNum))).map(
                        (el) => ({
                            value: el,
                            viewValue: el,
                        })
                    );

                    const protocolControl = controls?.find((p) => p.labelText === 'Protocol');
                    protocolControl.values = Array.from(new Set(this.data?.map((item) => item.protocol))).map((el) => ({
                        value: el,
                        viewValue: el,
                    }));

                    visitNameControl.values = Array.from(new Set(this.data?.map((item) => item.visitName))).map(
                        (el) => ({ value: el, viewValue: el })
                    );

                    controls = controls?.filter((f) => f.type != null);
                }
            }

            this.selectControls = controls;
            this.setFilters();

            this.createRows = this.selectControls;
        }
    }

    applyFilters(): void {
        let dataObject = this.separateObject(this.genericRange);

        this.filterClickEvent.emit(dataObject);
    }

    onApplyFiltersClicked() {
        if (this.isEmpty(this.genericRange)) return;

        this.filtersModifiedByUser = true;
        this.applyFilters();
    }

    private isEmpty(obj: Object): boolean {
        return Object.getOwnPropertyNames(obj).length === 0;
    }
    onResetFiltersClicked() {
        this.setToDefaults = !this.setToDefaults;
        this.resetFilter();
        this.filtersModifiedByUser = true;
    }
    resetFilter(): void {
        this.genericRange = {};
        this.resetDataClickEvent.emit();
    }

    addInputChange(e: any): void {
        if (e?.type === 'patientid') e.type = 'patientNum';
        if (e.range) {
            this.genericRange[e.type] = e.range;
            return;
        }
        if (e.event) this.genericRange[e.type] = e.event.value;
    }

    public separateObject = (obj: object): {}[] => {
        const result = [];
        const newObject = Object.entries(obj);
        for (const newObj of newObject) {
            if (!!newObj[1] && ((isArray(newObj[1]) && newObj[1]?.length > 0) || isArray(newObj[1]) === false)) {
                let createObj = {};
                createObj[newObj[0]] = newObj[1];
                result.push(createObj);
            }
        }
        return result;
    };

    getFilterName(): string {
        if (this.isPatientAllUrl()) return 'allPatientFilters';
        else if (this.isPatientOverviewUrl()) {
            return 'patientFilters';
        } else if (this.isManageViewUrl()) return 'accessRequestLogFilters';
        return 'requestFilters';
    }
}
